import { Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Trailer } from '~models/trailer.model';
import { TrailerService } from '~services/trailer.service';
import { TrailerType } from '~v2Models/well.model';
import { Subcarrier } from '~models/subcarrier.model';
import { ChoiceDialogComponent } from '../../ui-components/choice-dialog/choice-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'sa-trailer-management-active',
  templateUrl: './trailer-management-active.component.html',
  styleUrls: ['./trailer-management-active.component.scss'],
})
export class TrailerManagementActiveComponent implements OnInit {
  @Input() public trailers: Observable<Trailer[]>;
  @Input() public trailerTypes: Observable<TrailerType[]>;
  @Input() public subcarriers: Observable<Subcarrier[]>;
  @Input() public canManageTrailerManagement: BehaviorSubject<boolean>;
  public createEdit$$ = new BehaviorSubject<Trailer | {}>(null);
  public sortKey = 'trailerName';
  public sortOrder: 'asc' | 'desc' = 'asc';
  public sortedTrailers: Trailer[] = [];
  public sortIcons: { [key: string]: string } = {
    trailerName: '',
    trailerTypeName: '',
    trailerRegion: '',
    trailerStatus: '',
    subcarrierName: '',
    createdTimestamp: '',
  };
  public searchQuery = '';
  public selectedCategory = 'trailerName';

  constructor(private dialog: MatDialog, private trailerService: TrailerService, private snackbar: MatSnackBar) {}

  ngOnInit(): void {
    this.trailers.subscribe((trailers) => {
      this.sortedTrailers = trailers;
    });
  }

  sortTrailers(key: string) {
    if (this.sortKey === key) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortOrder = 'asc';
    }

    // Reset all icons
    for (const k in this.sortIcons) {
      if (this.sortIcons.hasOwnProperty(k)) {
        this.sortIcons[k] = '';
      }
    }

    // Set the current sort icon
    this.sortIcons[key] = this.sortOrder === 'asc' ? '▲' : '▼';

    this.sortedTrailers.sort((a, b) => {
      const aValue = a[key] ?? '';
      const bValue = b[key] ?? '';
      const comparison = aValue.localeCompare(bValue);
      return this.sortOrder === 'asc' ? comparison : -comparison;
    });
  }

  public closeNewTrailerForm() {
    this.createEdit$$.next(null);
  }

  editTrailer(trailer: Trailer) {
    this.createEdit$$.next(trailer);
  }

  promptDeleteTrailer(trailer: Trailer) {
    const dialogRef = this.dialog.open(ChoiceDialogComponent, {
      data: {
        button: ['No', 'Yes'],
        context: `Are you sure you want to remove ${trailer.trailerName}`,
        desc: '',
      },
      maxWidth: '968px',
      width: '30%',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.proceedDeleteTrailer(trailer);
      }
    });
  }

  async proceedDeleteTrailer(trailer) {
    const result = await this.trailerService.deleteTrailer(trailer.trailerId);
    if (result) {
      const message = `Trailer ${trailer.trailerName} successfully deleted`;
      this.snackbar.open(message, null, { duration: 3000 });
    } else {
      const message = `Failed to delete ${trailer.trailerName}`;
      this.snackbar.open(message, null, { duration: 3000, panelClass: ['snackbar-error'] });
    }
  }

  prettyStatus(s: string): string {
    switch (s.toLowerCase()) {
      case 'out_of_service':
        return 'Out of Service';
      case 'available':
        return 'Available';
      case 'rented':
        return 'Rented';
      case 'maintenance':
        return 'Maintenance';
      default:
        return s;
    }
  }

  filterTrailers() {
    const query = this.searchQuery.toLowerCase();
    this.trailers.subscribe((trailers) => {
      if (!trailers) {
        this.sortedTrailers = [];
        return;
      }

      if (!query) {
        this.sortedTrailers = trailers;
      } else {
        this.sortedTrailers = trailers.filter((trailer) => {
          const fieldValue = trailer[this.selectedCategory]?.toLowerCase() ?? '';
          return fieldValue.includes(query);
        });
      }
    });
  }

  clearSearch() {
    this.searchQuery = '';
    this.filterTrailers();
  }
}
